import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import {
  defaultStorage,
  sessionStorage as amplifySessionStorage,
} from "aws-amplify/utils";
import { desktop } from "./platform";

// HIPAA (§164.312 — access control / automatic logoff): on shared group-home or
// kiosk browsers, Cognito tokens must not linger after the session ends. We
// therefore default WEB token storage to sessionStorage (cleared when the tab /
// browser closes) and treat "Remember me" as an explicit opt-in to persist
// tokens in localStorage across restarts on a user's own device.
//
// Desktop (Electron) is exempt: US-013 AC10 already routes tokens to the OS
// keychain — the most secure persistent store on a trusted device — so the web
// sessionStorage/localStorage toggle does not apply there. The functions below
// no-op when the keychain store is active so they never stomp it.

const REMEMBER_ME_KEY = "ls.auth.rememberMe";

function readRememberFlag(): boolean {
  try {
    return window.localStorage.getItem(REMEMBER_ME_KEY) === "1";
  } catch {
    // localStorage unavailable (private mode / disabled) — fall back to the
    // more secure session-only storage.
    return false;
  }
}

function applyWebTokenStorage(rememberMe: boolean): void {
  cognitoUserPoolsTokenProvider.setKeyValueStorage(
    rememberMe ? defaultStorage : amplifySessionStorage,
  );
}

/**
 * Persist the user's "Remember me" choice and point Cognito's token provider at
 * the matching web storage. Call this immediately BEFORE `signIn()` so the
 * tokens Amplify writes land in the right place for this session.
 *
 * No-op on desktop, where tokens live in the OS keychain (US-013 AC10).
 */
export function setAuthPersistence(rememberMe: boolean): void {
  if (desktop?.tokenStore.available) return;
  try {
    window.localStorage.setItem(REMEMBER_ME_KEY, rememberMe ? "1" : "0");
  } catch {
    // Can't persist the preference — still apply it for this session below.
  }
  applyWebTokenStorage(rememberMe);
}

/**
 * Restore the saved persistence choice at app boot, BEFORE any token is read
 * (e.g. `getCurrentUser()` on load). Absent flag → session-only (secure
 * default). Web-only; desktop keeps the keychain store.
 */
export function restoreAuthPersistence(): void {
  if (desktop?.tokenStore.available) return;
  applyWebTokenStorage(readRememberFlag());
}
